import { memo, useEffect, useCallback } from 'react';
import { Button, Space, notification } from 'antd';
import { WarningFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'translations';
import { LINKS } from 'configuration';

interface UsageWarningProps {
  visible: boolean;
  points: {
    remaining_point?: number;
    remaining_percentage?: number;
  };
}

const UsageWarning = memo(({ visible, points }: UsageWarningProps) => {
  const { tC } = useTranslation('features', 'billingAlerts');
  const [api, contextHolder] = notification.useNotification();

  const navigate = useNavigate();
  const navigateTo = useCallback(
    (path: string) => {
      api.destroy('usageWarning');
      navigate(path, { replace: true });
    },
    [api, navigate],
  );

  const openNotification = useCallback(() => {
    const key = `usageWarning`;
    const btn = (
      <Space>
        <Button size="small" type="link" onClick={() => navigateTo(LINKS.activeSubscription)}>
          {tC('usageWarningCurrentSubscriptionButton')}
        </Button>
        <Button size="small" type="primary" onClick={() => navigateTo(LINKS.pricing)}>
          {tC('usageWarningUpgradeSubscriptionButton')}
        </Button>
      </Space>
    );

    api.warning({
      message: tC(`usageWarningTitle`),
      description: tC(`usageWarningDescription`, {
        points: points?.remaining_point,
        percentage: points?.remaining_percentage,
      }),
      btn,
      key,
      duration: 100,
    });
  }, [tC, api, points?.remaining_point, points?.remaining_percentage, navigateTo]);

  useEffect(() => {
    if (visible) {
      openNotification();
    } else {
      api.destroy('usageWarning');
    }
  }, [visible, openNotification, api]);

  if (!points || !visible) {
    return null;
  }

  return (
    <>
      {contextHolder}
      <WarningFilled
        onClick={openNotification}
        style={{ marginTop: 18, fontSize: 22, color: 'orange' }}
      />
    </>
  );
});

UsageWarning.displayName = 'UsageWarning';

export default UsageWarning;
